import { type RemovableRef, useSessionStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

type Link = {
  url: string
  product_name: string
  product_description: string
  payment_code: string
  price: number
  product_id: string
}

type State = {
  page: string
  code: string
  email: string
  links: RemovableRef<Link[]>
}

export const useAppStore = defineStore('app', {
  state: (): State => ({
    //
    page: '',
    code: '',
    email: '',
    links: useSessionStorage('links', [] as Link[]),
  }),
  getters: {
    hasCode: state => !!state.code,
    linkCount: state => state.links.length,
  },
  actions: {
    async getProductList () {
      return await this._fetchJson('/products')
    },
    async getPriceList () {
      return await this._fetchJson('/prices')
    },
    async getPaymentLinksForCode () {
      return await this._fetchJson('/payment_links_by_code', {
        method: 'POST',
        body: JSON.stringify({
          paymentCode: this.code,
          sandbox: import.meta.env.VITE_SANDBOX,
        }),
      })
    },
    async unlockSubscription () {
      if (!this.email || !this.code) {
        throw new Error('Email and code must be provided')
      }
      return await this._fetchJson('/unlock_subscription', {
        method: 'POST',
        body: JSON.stringify({
          paymentCode: this.code.toUpperCase(),
          email: this.email,
        }),
      })
    },
    async _fetchJson (endpoint: string, options: RequestInit = {}) {
      const baseUrl = import.meta.env.VITE_BASE_URL ?? ''
      const defaultHeaders = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
      const mergedOptions: RequestInit = {
        method: options.method ?? 'GET',
        headers: { ...defaultHeaders, ...options.headers },
        ...options,
      }
      const response = await fetch(`${baseUrl}${endpoint}`, mergedOptions)
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }
      return await response.json()
    },
  },
})
